import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WorkshopDetailModal = ({ workshop, onClose, onEnroll }) => {
  const [selectedSession, setSelectedSession] = useState(null);

  if (!workshop) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEnroll = () => {
    if (selectedSession) {
      onEnroll(workshop, selectedSession);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-semibold text-foreground">Workshop Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-hover"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="relative h-64 rounded-lg overflow-hidden mb-6">
            <Image
              src={workshop?.image}
              alt={workshop?.imageAlt}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <h3 className="text-xl font-semibold text-foreground mb-4">
                {workshop?.title}
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {workshop?.fullDescription}
              </p>

              <div className="mb-6">
                <h4 className="text-lg font-semibold text-foreground mb-3">
                  What You'll Learn
                </h4>
                <ul className="space-y-2">
                  {workshop?.learningOutcomes?.map((outcome, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Icon name="Check" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{outcome}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {workshop?.prerequisites && workshop?.prerequisites?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-foreground mb-3">
                    Prerequisites
                  </h4>
                  <ul className="space-y-2">
                    {workshop?.prerequisites?.map((prereq, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{prereq}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-muted/50 rounded-lg p-6 sticky top-24">
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
                  <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                    <Image
                      src={workshop?.organizerImage || workshop?.instructorImage}
                      alt={workshop?.organizerImageAlt || workshop?.instructorImageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground mb-1">
                      {workshop?.instructor}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {workshop?.instructorTitle}
                    </p>
                    {workshop?.verified && (
                      <div className="flex items-center gap-1 mt-1">
                        <Icon name="BadgeCheck" size={14} color="var(--color-primary)" />
                        <span className="text-xs text-primary">Verified</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Icon name="Users" size={20} color="var(--color-muted-foreground)" />
                    <div>
                      <p className="text-xs text-muted-foreground">Participants</p>
                      <p className="text-sm font-medium text-foreground">{workshop?.participants || 0} enrolled</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Icon name="Ticket" size={20} color="var(--color-muted-foreground)" />
                    <div>
                      <p className="text-xs text-muted-foreground">Spots left</p>
                      <p className="text-sm font-medium text-foreground">
                        {workshop?.spotsLeft === 0 ? 'No limit' : (workshop?.spotsLeft ?? '—')}
                      </p>
                    </div>
                  </div>
                  {workshop?.format === 'physical' && workshop?.address && (
                    <div className="flex items-start gap-3">
                      <Icon name="MapPin" size={20} color="var(--color-muted-foreground)" className="mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="text-sm font-medium text-foreground">{workshop?.address}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-foreground mb-3">
                    Select Session
                  </h4>
                  <div className="space-y-2">
                    {workshop?.upcomingSessions?.map((session, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedSession(session)}
                        className={`
                          w-full p-3 rounded-md border text-left transition-smooth
                          ${selectedSession === session
                            ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                          }
                        `}
                      >
                        <p className="text-sm font-medium text-foreground mb-1">
                          {formatDate(session?.date)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(session?.spotsLeft ?? workshop?.spotsLeft ?? 0) === 0 
                            ? 'No limit' 
                            : `${session?.spotsLeft ?? workshop?.spotsLeft ?? '—'} spots left`}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  variant={workshop?.enrolled ? 'success' : 'default'}
                  fullWidth
                  iconName={workshop?.enrolled ? 'Check' : 'Plus'}
                  iconPosition="left"
                  onClick={handleEnroll}
                  disabled={workshop?.enrolled || !selectedSession}
                >
                  {workshop?.enrolled ? 'Already Enrolled' : 'Enroll Now'}
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WorkshopDetailModal;